import { useState, useCallback, useMemo, type FormEvent } from 'react';
import { Button, Card, Input } from '../ui';

export interface RegisterScreenProps {
  /** Called when user submits registration form */
  onRegister?: (email: string, password: string) => Promise<void>;
  /** Called when user navigates back to login */
  onNavigateLogin?: () => void;
}

interface PasswordRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: '8–128 characters',
    test: (p) => p.length >= 8 && p.length <= 128,
  },
  {
    id: 'uppercase',
    label: 'At least one uppercase letter',
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: 'lowercase',
    label: 'At least one lowercase letter',
    test: (p) => /[a-z]/.test(p),
  },
  {
    id: 'digit',
    label: 'At least one digit',
    test: (p) => /\d/.test(p),
  },
];

/**
 * Validates a password against all rules.
 * Returns true if all rules pass.
 */
export function validatePassword(password: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password));
}

/**
 * Registration screen with email/password form and real-time password validation feedback.
 * Shows which password requirements are met/unmet as the user types.
 *
 * @see Requirements 7.1, 7.3
 */
export function RegisterScreen({
  onRegister,
  onNavigateLogin,
}: RegisterScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const passwordRuleResults = useMemo(
    () =>
      PASSWORD_RULES.map((rule) => ({
        ...rule,
        met: rule.test(password),
      })),
    [password]
  );

  const isPasswordValid = useMemo(
    () => passwordRuleResults.every((r) => r.met),
    [passwordRuleResults]
  );

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const isFormValid = email.trim().length > 0 && isPasswordValid && passwordsMatch;

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!email.trim()) {
        setError('Please enter your email address');
        return;
      }

      if (!isPasswordValid) {
        setError('Password does not meet the requirements');
        return;
      }

      if (!passwordsMatch) {
        setError('Passwords do not match');
        return;
      }

      setIsLoading(true);
      try {
        await onRegister?.(email.trim(), password);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Registration failed. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, isPasswordValid, passwordsMatch, onRegister]
  );

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <h1 className="text-heading-1 text-text-heading font-bold">Create Account</h1>
              <p className="mt-2 text-sm text-text-secondary">
                Start your journaling journey
              </p>
            </div>

            {/* Error announcement */}
            <div aria-live="polite" aria-atomic="true">
              {error && (
                <div
                  className="rounded-sm bg-primary-light/30 border border-primary-dark/20 px-4 py-3 text-sm text-primary-dark"
                  role="alert"
                >
                  {error}
                </div>
              )}
            </div>

            {/* Registration form */}
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="email"
                required
                aria-required="true"
              />

              <div className="space-y-2">
                <Input
                  label="Password"
                  type="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  required
                  aria-required="true"
                  aria-describedby="password-requirements"
                />

                {/* Password validation feedback */}
                {password.length > 0 && (
                  <ul
                    id="password-requirements"
                    className="space-y-1 text-sm"
                    aria-label="Password requirements"
                  >
                    {passwordRuleResults.map((rule) => (
                      <li
                        key={rule.id}
                        className={`flex items-center gap-2 ${
                          rule.met ? 'text-success' : 'text-text-secondary'
                        }`}
                        aria-label={`${rule.label}: ${rule.met ? 'met' : 'not met'}`}
                      >
                        <span aria-hidden="true" className="text-xs">
                          {rule.met ? '✓' : '○'}
                        </span>
                        <span>{rule.label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <Input
                label="Confirm Password"
                type="password"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="new-password"
                required
                aria-required="true"
                error={
                  confirmPassword.length > 0 && !passwordsMatch
                    ? 'Passwords do not match'
                    : undefined
                }
              />

              <Button
                type="submit"
                className="w-full"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            {/* Login link */}
            <p className="text-center text-sm text-text-secondary">
              Already have an account?{' '}
              <button
                type="button"
                className="text-primary-dark font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm"
                onClick={onNavigateLogin}
              >
                Sign in
              </button>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
